/**
 * Price utility functions for Wonder Herbals.
 * Handles INR and USD formatting, discount calculations, and GST.
 * All prices are stored as integers in paise (INR) or cents (USD)
 * to avoid floating-point precision issues.
 */

import type { Currency } from '../types';

// ============================================================
// Paise/Cents ↔ Rupees/Dollars Conversion
// ============================================================

/**
 * Convert paise/cents to base currency unit (rupees/dollars).
 * @example paiseToBase(199900) // 1999.00
 */
export function paiseToBase(paise: number): number {
    return paise / 100;
}

/**
 * Convert base currency unit (rupees/dollars) to paise/cents.
 * @example baseToPaise(1999.00) // 199900
 */
export function baseToPaise(base: number): number {
    return Math.round(base * 100);
}

// ============================================================
// Formatting
// ============================================================

/**
 * Format a price in paise as INR string with proper Indian numbering system.
 * Uses the Indian grouping: 1,00,000 instead of 100,000.
 *
 * @example formatINR(199900) // "₹1,999"
 * @example formatINR(199900, true) // "₹1,999.00"
 * @example formatINR(10000000) // "₹1,00,000"
 */
export function formatINR(paise: number, showDecimals = false): string {
    const rupees = paiseToBase(paise);
    const formatted = formatIndianNumber(rupees, showDecimals ? 2 : 0);
    return `₹${formatted}`;
}

/**
 * Format a price in cents as USD string.
 *
 * @example formatUSD(2999) // "$29.99"
 * @example formatUSD(2999, false) // "$30"
 */
export function formatUSD(cents: number, showDecimals = true): string {
    const dollars = paiseToBase(cents);
    return `$${dollars.toLocaleString('en-US', {
        minimumFractionDigits: showDecimals ? 2 : 0,
        maximumFractionDigits: showDecimals ? 2 : 0,
    })}`;
}

/**
 * Format price based on currency.
 */
export function formatPrice(
    paise: number,
    currency: Currency = 'INR',
    showDecimals?: boolean
): string {
    if (currency === 'USD') {
        return formatUSD(paise, showDecimals ?? true);
    }
    return formatINR(paise, showDecimals ?? false);
}

/**
 * Format a number using the Indian numbering system.
 * Indian system: 1,00,000 (lakhs) instead of 100,000
 *
 * @example formatIndianNumber(100000) // "1,00,000"
 * @example formatIndianNumber(1999, 2) // "1,999.00"
 */
export function formatIndianNumber(
    value: number,
    decimalPlaces = 0
): string {
    const fixed = value.toFixed(decimalPlaces);
    const parts = fixed.split('.');
    const intPart = parts[0] || '0';
    const decPart = parts[1];

    // Indian number formatting: last 3 digits, then groups of 2
    const isNegative = intPart.startsWith('-');
    const absInt = isNegative ? intPart.slice(1) : intPart;

    let result: string;

    if (absInt.length <= 3) {
        result = absInt;
    } else {
        const lastThree = absInt.slice(-3);
        const remaining = absInt.slice(0, -3);
        const formatted = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
        result = `${formatted},${lastThree}`;
    }

    if (isNegative) {
        result = `-${result}`;
    }

    if (decPart) {
        result = `${result}.${decPart}`;
    }

    return result;
}

// ============================================================
// Discount Calculations
// ============================================================

/**
 * Calculate discount percentage between price and compare-at price.
 *
 * @example calculateDiscountPercent(199900, 249900) // 20
 */
export function calculateDiscountPercent(
    price: number,
    compareAtPrice: number
): number {
    if (compareAtPrice <= 0 || price >= compareAtPrice) {
        return 0;
    }
    return Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
}

/**
 * Calculate discount amount in paise.
 *
 * @example calculateDiscountAmount(199900, 249900) // 50000
 */
export function calculateDiscountAmount(
    price: number,
    compareAtPrice: number
): number {
    if (price >= compareAtPrice) {
        return 0;
    }
    return compareAtPrice - price;
}

// ============================================================
// GST Calculations
// ============================================================

/**
 * Common GST rates in India.
 */
export const GST_RATES = {
    ZERO: 0,
    FIVE: 5,
    TWELVE: 12,
    EIGHTEEN: 18,
    TWENTY_EIGHT: 28,
} as const;

/**
 * Calculate GST amount from inclusive price.
 * If the price includes GST, this extracts the GST component.
 *
 * @example calculateGSTInclusive(11800, 18) // 1800 (₹118 inclusive → ₹100 base + ₹18 GST)
 */
export function calculateGSTInclusive(
    priceInclusive: number,
    gstRate: number
): number {
    if (gstRate <= 0) {
        return 0;
    }
    return Math.round(priceInclusive - priceInclusive * (100 / (100 + gstRate)));
}

/**
 * Calculate GST amount from exclusive price.
 * Adds GST on top of the base price.
 *
 * @example calculateGSTExclusive(10000, 18) // 1800 (₹100 + 18% = ₹118)
 */
export function calculateGSTExclusive(
    priceExclusive: number,
    gstRate: number
): number {
    if (gstRate <= 0) {
        return 0;
    }
    return Math.round(priceExclusive * (gstRate / 100));
}

/**
 * Get the base price (before GST) from an inclusive price.
 *
 * @example getBasePriceFromInclusive(11800, 18) // 10000
 */
export function getBasePriceFromInclusive(
    priceInclusive: number,
    gstRate: number
): number {
    return priceInclusive - calculateGSTInclusive(priceInclusive, gstRate);
}

// ============================================================
// Coupon Calculations
// ============================================================

/**
 * Calculate coupon discount amount.
 *
 * @param type - 'percentage' or 'fixed'
 * @param value - Discount value (percentage number or fixed amount in paise)
 * @param subtotal - Order subtotal in paise
 * @param maxDiscount - Maximum discount cap in paise (for percentage coupons)
 * @returns Discount amount in paise
 */
export function calculateCouponDiscount(
    type: 'percentage' | 'fixed',
    value: number,
    subtotal: number,
    maxDiscount?: number
): number {
    if (type === 'fixed') {
        return Math.min(value, subtotal);
    }

    // Percentage discount
    let discount = Math.round(subtotal * (value / 100));

    if (maxDiscount && discount > maxDiscount) {
        discount = maxDiscount;
    }

    return Math.min(discount, subtotal);
}

// ============================================================
// Shipping Calculations
// ============================================================

/**
 * Calculate shipping cost based on weight and delivery zone.
 * Simplified version — actual rates come from Shiprocket API.
 */
export function calculateShippingCost(
    weightGrams: number,
    isFreeShipping: boolean,
    freeShippingThreshold?: number,
    orderSubtotal?: number
): number {
    if (isFreeShipping) {
        return 0;
    }

    if (
        freeShippingThreshold &&
        orderSubtotal &&
        orderSubtotal >= freeShippingThreshold
    ) {
        return 0;
    }

    // Base shipping: ₹49 for orders under 500g, ₹99 for heavier
    if (weightGrams <= 500) {
        return baseToPaise(49);
    }
    return baseToPaise(99);
}

// ============================================================
// Order Number Generation
// ============================================================

/**
 * Generate a human-readable order number.
 * Format: WH-YYYY-NNNNN (e.g., WH-2024-00001)
 */
export function generateOrderNumber(sequenceNumber: number): string {
    const year = new Date().getFullYear();
    const padded = sequenceNumber.toString().padStart(5, '0');
    return `WH-${year}-${padded}`;
}
